import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { M3_NAV } from "../data/nav";
import { portfolio } from "../data/portfolio";
import type { Theme } from "../hooks/useTheme";

interface Props {
  theme: Theme;
  onToggleTheme: () => void;
  onOpenSearch: () => void;
}

export function AppBar({ theme, onToggleTheme, onOpenSearch }: Props) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const { identity } = portfolio;
  const initials = identity.name.split(" ").slice(0, 2).map((s) => s[0]).join("");

  return (
    <>
      <header className={"md-appbar " + (scrolled ? "is-scrolled" : "")}>
        <div className="md-container md-appbar__row">
          <Link to="/" className="md-appbar__brand" title="Back to landing">
            <span className="md-appbar__logo md-title-m">{initials}</span>
            <div className="md-appbar__brand-text">
              <div className="md-title-m md-appbar__brand-name">{identity.name}</div>
              <div className="md-body-s dim md-appbar__brand-role">{identity.role}</div>
            </div>
          </Link>

          <div className="md-appbar__center">
            <button
              type="button"
              className="md-searchbar"
              onClick={onOpenSearch}
              style={{ width: 420, maxWidth: "100%" }}
            >
              <span className="material-symbols-rounded">search</span>
              <span className="md-body-m dim" style={{ flex: 1, textAlign: "left" }}>
                Search the portfolio
              </span>
              <kbd
                style={{
                  fontFamily: "var(--md-sys-typescale-mono)",
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: 4,
                  background: "var(--md-sys-color-surface-container)",
                  color: "var(--md-sys-color-on-surface-variant)",
                }}
              >
                /
              </kbd>
            </button>
          </div>

          <div className="md-appbar__actions">
            <a
              className="md-btn md-btn--icon md-appbar__hide-md"
              href={identity.links.github}
              target="_blank"
              rel="noreferrer"
              aria-label="GitHub"
            >
              <span className="material-symbols-rounded">code</span>
            </a>
            <a
              className="md-btn md-btn--icon md-appbar__hide-md"
              href={identity.links.linkedin}
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
            >
              <span className="material-symbols-rounded">groups</span>
            </a>
            <button
              type="button"
              className="md-btn md-btn--icon"
              onClick={onToggleTheme}
              aria-label="Toggle theme"
            >
              <span className="material-symbols-rounded">
                {theme === "dark" ? "light_mode" : "dark_mode"}
              </span>
            </button>
            <a
              className="md-btn md-btn--tonal md-appbar__resume"
              href={identity.links.cv}
              target="_blank"
              rel="noreferrer"
              aria-label="View résumé"
            >
              <span className="material-symbols-rounded">open_in_new</span>
              <span className="md-appbar__resume-label">Résumé</span>
            </a>
          </div>
        </div>
      </header>

      <nav className={"md-subnav " + (scrolled ? "is-scrolled" : "")}>
        <div className="md-container md-subnav__row">
          <div className="md-chip-group md-chip-group--scroll" role="tablist">
            {M3_NAV.map((n) => (
              <a
                key={n.id}
                href={"#" + n.id}
                className="md-chip md-chip--assist"
                onClick={(e) => {
                  // HashRouter would interpret `#about` as a route change and
                  // fall through to the catch-all. Scroll the section into
                  // view manually instead and leave the router alone.
                  e.preventDefault();
                  document
                    .getElementById(n.id)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                <span className="material-symbols-rounded icon-20">{n.icon}</span>
                {n.label}
              </a>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
}
